import Vue from "vue"
import {
  AVAILABLE_BOUNTIES_PAGE_SIZE,
  HIDDEN_BOUNTIES_LOCAL_STORAGE_KEY,
} from "./bounties.constants"
import {
  acceptBounty,
  fetchAcceptedBounties,
  fetchAvailableBounties,
  fetchGameById,
  hideBounty,
} from "./bounties.apis"
import {
  execOpWithAuth,
  mapBountyToRequest,
} from "./bounties.utils"

const state = {
  acceptedBountyHistory: [],
  availableBounties: [],
  availableBountiesCursor: '',
  canLoadMoreAvailableBounties: true,
  isLoadingMoreAvailableBounties: false,
  hiddenBounties: {},
  gamesById: {},
}

const mutations = {
  SET_ACCEPTED_BOUNTY_HISTORY: (state, acceptedBounties) => {
    state.acceptedBountyHistory = acceptedBounties
  },
  SET_AVAILABLE_BOUNTIES: (state, availableBounties) => {
    state.availableBounties = availableBounties
  },
  SET_NEXT_AVAILABLE_BOUNTIES_CURSOR: (state, cursor) => {
    state.availableBountiesCursor = cursor
  },
  SET_CAN_LOAD_MORE_AVAILABLE_BOUNTIES: (state, canLoad) => {
    state.canLoadMoreAvailableBounties = canLoad
  },
  SET_IS_LOADING_MORE_AVAILABLE_BOUNTIES: (state, isLoading) => {
    state.isLoadingMoreAvailableBounties = isLoading
  },
  MOVE_AVAILABLE_BOUNTY_TO_ACCEPTED: (state, acceptedBounty) => {
    const availableBountyIndex = state.availableBounties.findIndex((bounty) => bounty.id === acceptedBounty.id)
    if (availableBountyIndex !== -1) {
      state.availableBounties.splice(availableBountyIndex, 1)
    }

    state.acceptedBountyHistory.unshift(acceptedBounty)
  },
  SET_HIDDEN_BOUNTIES: (state, hiddenBounties) => {
    state.hiddenBounties = hiddenBounties
  },
  HIDE_BOUNTY: (state, bountyId) => {
    Vue.set(state.hiddenBounties, bountyId, 1)
    // Persist in local storage
    window.localStorage.setItem(HIDDEN_BOUNTIES_LOCAL_STORAGE_KEY, JSON.stringify(state.hiddenBounties))
  },
  SAVE_GAME: (state, game) => {
    const gameId = game.id
    if (!gameId) {
      return
    }
    Vue.set(state.gamesById, gameId, game)
  },
}

const actions = {
  fetchGameById: async ({ state, commit, dispatch }, gameId) => {
    if (gameId in state.gamesById) {
      return
    }

    const game = await fetchGameById(gameId)
    if (game) {
      commit("SAVE_GAME", game)
    }
  },
  fetchAcceptedBounties: async ({ getters, rootGetters, commit, dispatch }) => {
    return execOpWithAuth(() => {
      return (async () => {
        const accessToken = rootGetters["Coach/Dashboard/AimlabsMarketplace/accessToken"]
        const acceptedBounties = await fetchAcceptedBounties({ accessToken })
        if (!acceptedBounties) {
          return
        }

        for (const bounty of acceptedBounties) {
          await Promise.all([
            dispatch("fetchGameById", bounty.gameId)
          ])
        }

        commit("SET_ACCEPTED_BOUNTY_HISTORY", acceptedBounties.map((bounty) => ({
          ...bounty,
          game: getters.getGameById(bounty.gameId),
        })))
      })()
    }, (e) => {
      console.error("Failed to fetch accepted bounties", e)
    }, dispatch)
  },
  fetchNextAvailableBountiesPage: async ({ state, getters, rootGetters, commit, dispatch }) => {
    if (state.isLoadingMoreAvailableBounties || !state.canLoadMoreAvailableBounties) {
      return
    }

    return execOpWithAuth(() => {
      return (async () => {
        commit("SET_IS_LOADING_MORE_AVAILABLE_BOUNTIES", true)

        const accessToken = rootGetters["Coach/Dashboard/AimlabsMarketplace/accessToken"]
        const availableBountiesPage = await fetchAvailableBounties({
          accessToken,
          limit: AVAILABLE_BOUNTIES_PAGE_SIZE,
          cursor: state.availableBountiesCursor,
        })
        if (!availableBountiesPage) {
          commit("SET_CAN_LOAD_MORE_AVAILABLE_BOUNTIES", false)
          return
        }
        const availableBounties = availableBountiesPage.result
        const nextCursor = availableBountiesPage.nextCursor ?? ''

        for (const bounty of availableBounties) {
          await Promise.all([
            dispatch("fetchGameById", bounty.gameId)
          ])
        }

        commit("SET_AVAILABLE_BOUNTIES", state.availableBounties.concat(availableBounties.map((bounty) => ({
          ...bounty,
          game: getters.getGameById(bounty.gameId),
        }))))
        commit("SET_NEXT_AVAILABLE_BOUNTIES_CURSOR", nextCursor)
        commit("SET_IS_LOADING_MORE_AVAILABLE_BOUNTIES", false)
      })()
    }, (e) => {
      console.error("Failed to fetch available bounties", e)
      commit("SET_IS_LOADING_MORE_AVAILABLE_BOUNTIES", false)
    }, dispatch)
  },
  acceptBounty: async ({ getters, rootGetters, commit, dispatch }, bountyId) => {
    const accessToken = rootGetters["Coach/Dashboard/AimlabsMarketplace/accessToken"]
    return execOpWithAuth(() => {
      return (async () => {
        const acceptedBounty = await acceptBounty({ accessToken, bountyId })

        await Promise.all([
          dispatch("fetchGameById", acceptedBounty.gameId)
        ])

        commit("MOVE_AVAILABLE_BOUNTY_TO_ACCEPTED", acceptedBounty)

        await dispatch(
          "Instapro/Vod/Coach/setEditingRequest",
          mapBountyToRequest({
            ...acceptedBounty,
            game: getters.getGameById(acceptedBounty.gameId),
          }),
          { root: true }
        )
      })()
    }, (e) => {
      console.error("Failed to accept bounty", e)
    }, dispatch)
  },
  loadHiddenBounties: ({ commit }) => {
    let hiddenBounties
    try {
      hiddenBounties = JSON.parse(window.localStorage.getItem(HIDDEN_BOUNTIES_LOCAL_STORAGE_KEY)) ?? {}
    } catch (e) {
      hiddenBounties = {}
    }
    commit("SET_HIDDEN_BOUNTIES", hiddenBounties)
  },
  hideBounty: async ({ getters, rootGetters, commit, dispatch }, bountyId) => {
    const accessToken = rootGetters["Coach/Dashboard/AimlabsMarketplace/accessToken"]
    return execOpWithAuth(() => {
      return (async () => {
        const creatorHiddenBounty = await hideBounty({ accessToken, bountyId })
        commit("HIDE_BOUNTY", creatorHiddenBounty.bountyId)
      })()
    }, (e) => {
      console.error("Failed to hide bounty", e)
    }, dispatch)
  },
}

const getters = {
  acceptedBounties: (state) => {
    return state.acceptedBountyHistory.filter((bounty) => {
      return !(bounty.id in state.hiddenBounties)
    })
  },
  availableBounties: (state) => {
    return state.availableBounties.filter((bounty) => {
      const bountyId = bounty.id
      const isHidden = bountyId in state.hiddenBounties
      const isAccepted = !!state.acceptedBountyHistory.find((acceptedBounty) => {
        return acceptedBounty.id === bountyId
      })
      return !isHidden && !isAccepted
    })
  },
  canLoadMoreAvailableBounties: (state) => {
    return state.canLoadMoreAvailableBounties
  },
  isLoadingMoreAvailableBounties: (state) => {
    return state.isLoadingMoreAvailableBounties
  },
  getGameById: (state) => (gameId) => {
    return state.gamesById[gameId] ?? null
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/coach/dashboard/aimlabs-marketplace/bounties/bounties.module.js